import { useState, useEffect, useRef } from 'react'
import { profileService, teamService } from '../../services'
import { useAuthStore } from '../../stores'
import { Button, Input, Spinner } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import type { SocialLinks } from '../../types/database.types'
import type { Organization } from '../../services/teamService'

// Social platform configuration
const SOCIAL_PLATFORMS = [
  { key: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/username' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/username' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/username' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/username' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@channel' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@username' },
  { key: 'threads', label: 'Threads', placeholder: 'https://threads.net/@username' },
] as const

export function ProfileSettingsPage() {
  const { profile, setProfile } = useAuthStore()
  const toast = useToast()
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)

  // Form state
  const [fullName, setFullName] = useState('')
  const [bio, setBio] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [location, setLocation] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [isPublicProfile, setIsPublicProfile] = useState(false)
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({})
  const [orgSocialLinks, setOrgSocialLinks] = useState<SocialLinks>({})

  // Expandable sections
  const [showIndividualSocial, setShowIndividualSocial] = useState(false)
  const [showCorporateSocial, setShowCorporateSocial] = useState(false)

  // Organization state (for corporate users)
  const [organization, setOrganization] = useState<Organization | null>(null)

  // Corporate tier detection
  const isCorporate =
    profile?.subscription_tier === 'corporate' ||
    profile?.subscription_tier === 'platinum'

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '')
      setBio(profile.bio || '')
      setSpecialty(profile.specialty || '')
      setLocation(profile.location || '')
      setCompanyName(profile.company_name || '')
      setWebsiteUrl(profile.website_url || '')
      setIsPublicProfile(profile.is_public_profile || false)
      setSocialLinks((profile.social_links as SocialLinks) || {})
    }
  }, [profile])

  // Load organization for corporate users
  useEffect(() => {
    const loadOrganization = async () => {
      if (isCorporate && profile) {
        try {
          const org = await teamService.getOrCreateOrganization(profile.id, profile.full_name || 'My Team')
          if (org) {
            setOrganization(org)
            // Auto-fill company name and website from organization (read-only)
            setCompanyName(org.name)
            if (org.website_url) {
              setWebsiteUrl(org.website_url)
            }
            setOrgSocialLinks((org.social_links as SocialLinks) || {})
          }
        } catch (error) {
          console.error('Failed to load organization:', error)
        }
      }
    }
    loadOrganization()
  }, [isCorporate, profile])

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !profile) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be smaller than 2MB')
      return
    }

    setIsUploadingAvatar(true)

    try {
      const avatarUrl = await profileService.uploadAvatar(profile.id, file)
      if (avatarUrl) {
        setProfile({ ...profile, avatar_url: avatarUrl })
        toast.success('Avatar updated successfully')
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error)
      toast.error('Failed to upload avatar')
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!profile) return

    setIsLoading(true)

    try {
      // Build update object (exclude corporate-controlled fields if corporate user)
      const updateData: Record<string, unknown> = {
        full_name: fullName.trim() || null,
        bio: bio.trim() || null,
        specialty: specialty.trim() || null,
        location: location.trim() || null,
        is_public_profile: isPublicProfile,
        social_links: socialLinks,
      }

      // Only update company_name and website_url if not corporate
      if (!isCorporate) {
        updateData.company_name = companyName.trim() || null
        updateData.website_url = websiteUrl.trim() || null
      }

      const updatedProfile = await profileService.updateProfile(profile.id, updateData)

      // Update organization social links if corporate
      if (isCorporate && organization) {
        await teamService.updateOrganization(organization.id, {
          social_links: orgSocialLinks,
        })
      }

      if (updatedProfile) {
        setProfile(updatedProfile)
        toast.success('Profile updated successfully')
      }
    } catch (error) {
      console.error('Failed to update profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  // Helper to update individual social link
  const updateSocialLink = (platform: string, value: string) => {
    setSocialLinks((prev: SocialLinks) => ({ ...prev, [platform]: value || undefined }))
  }

  // Helper to update corporate social link
  const updateOrgSocialLink = (platform: string, value: string) => {
    setOrgSocialLinks((prev: SocialLinks) => ({ ...prev, [platform]: value || undefined }))
  }

  if (!profile) {
    return null
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p className="mt-1 text-gray-600">
          Manage your public profile and account details.
        </p>
      </div>

      {/* Avatar section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Profile Photo
        </h2>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.full_name || 'Profile'}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl font-medium text-gray-600">
                {profile.full_name?.[0]?.toUpperCase() || 'P'}
              </span>
            )}
          </div>
          <div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => avatarInputRef.current?.click()}
              disabled={isUploadingAvatar}
            >
              {isUploadingAvatar ? (
                <>
                  <Spinner size="sm" />
                  <span className="ml-2">Uploading...</span>
                </>
              ) : (
                'Change Photo'
              )}
            </Button>
            <p className="mt-2 text-sm text-gray-500">
              JPG, PNG or GIF. Max 2MB.
            </p>
          </div>
        </div>
      </div>

      {/* Profile form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Basic Information
          </h2>

          <Input
            label="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="John Doe"
          />

          <Input
            label="Specialty"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            placeholder="e.g., Physical Therapist, Strength Coach"
          />

          <Input
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., New York, NY"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell others about yourself and your expertise..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              {bio.length}/500 characters
            </p>
          </div>

          {/* Company Name */}
          <div>
            <Input
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g., Peak Performance PT"
              disabled={isCorporate}
            />
            {isCorporate && (
              <p className="mt-1 text-sm text-gray-500">
                Managed by your organization. Edit in Team settings.
              </p>
            )}
          </div>

          {/* Website URL */}
          <div>
            <Input
              label="Website URL"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://your-website.com"
              disabled={isCorporate}
            />
            {isCorporate && (
              <p className="mt-1 text-sm text-gray-500">
                Managed by your organization. Edit in Team settings.
              </p>
            )}
          </div>

          {/* Individual Social Links - Expandable */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowIndividualSocial(!showIndividualSocial)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-medium text-gray-900">Social Links</span>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  showIndividualSocial ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showIndividualSocial && (
              <div className="p-4 space-y-4 border-t border-gray-200">
                {SOCIAL_PLATFORMS.map((platform) => (
                  <Input
                    key={platform.key}
                    label={platform.label}
                    type="url"
                    value={socialLinks[platform.key as keyof SocialLinks] || ''}
                    onChange={(e) => updateSocialLink(platform.key, e.target.value)}
                    placeholder={platform.placeholder}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Corporate Social Links - Only for corporate users */}
          {isCorporate && (
            <div className="border border-blue-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowCorporateSocial(!showCorporateSocial)}
                className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-blue-900">Corporate Social Links</span>
                  <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                    Organization
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-blue-500 transition-transform ${
                    showCorporateSocial ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showCorporateSocial && (
                <div className="p-4 space-y-4 border-t border-blue-200">
                  <p className="text-sm text-blue-700 mb-4">
                    These links are shared across all team members in your organization.
                  </p>
                  {SOCIAL_PLATFORMS.map((platform) => (
                    <Input
                      key={platform.key}
                      label={platform.label}
                      type="url"
                      value={orgSocialLinks[platform.key as keyof SocialLinks] || ''}
                      onChange={(e) => updateOrgSocialLink(platform.key, e.target.value)}
                      placeholder={platform.placeholder}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Privacy settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Privacy Settings
          </h2>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isPublicProfile}
              onChange={(e) => setIsPublicProfile(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <p className="font-medium text-gray-900">Public Profile</p>
              <p className="text-sm text-gray-500">
                Allow others to view your profile and public videos. Your
                profile will appear in search results and can be linked from
                your videos.
              </p>
            </div>
          </label>
        </div>

        {/* Account info (read-only) */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Account Information
          </h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-gray-500">Email</dt>
              <dd className="text-gray-900">{profile.email}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Role</dt>
              <dd className="text-gray-900 capitalize">
                {profile.role?.replace('_', ' ') || 'Unknown'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Subscription</dt>
              <dd className="text-gray-900 capitalize">
                {profile.subscription_tier?.replace('_', ' ') || 'Free'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Member Since</dt>
              <dd className="text-gray-900">
                {new Date(profile.created_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner size="sm" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
